import { styles } from './detailsModal-styles'
import React, { useEffect, useRef, useState } from 'react'
import Button from '../../../blocks.simple/button/button'
import translate from '../../../core/translate'
import Icon from '../../../blocks.simple/icon/icon'
import { api } from '../../../core/api/ConnectionManager'
import LoaderItem from '../../../blocks.app/loader/__item/loader__item'
import { ApiEventsType } from '../../../core/api/events'

type JsonPrimitive = string | number | boolean | null
type JsonValue = JsonPrimitive | JsonObject | JsonValue[]

interface JsonObject {
    [key: string]: JsonValue
}

interface DiffPath {
    path: string
    line: number
}

type Config = {
    currentPath: string[]
    out: string
    line: number
    indent: number
    paths: DiffPath[]
}

interface Diff {
    path1: DiffPath
    path2: DiffPath
    type: string
    msg: string
}

interface ComparedData {
    before: string
    after: string
}

interface DataToCompare {
    before: JsonValue
    after: JsonValue
}

interface DiffElement {
    index: number
    top: number
    element: HTMLElement
}

interface DiffLines {
    left: DiffElement[]
    right: DiffElement[]
}

interface DetailsModalProps {
    id: number | string
    onClose: () => void
    methodName?: ApiEventsType
    options?: Record<string, unknown>
}

interface HistoryDetailsResponse {
    before: JsonValue
    after: JsonValue
}

let jdd: {
    LEFT: string
    RIGHT: string
    EQUALITY: string
    TYPE: string
    MISSING: string
    diffs: Diff[]
    SEPARATOR: string
    requestCount: number
} = {
    LEFT: 'left',
    RIGHT: 'right',
    EQUALITY: 'eq',
    TYPE: 'type',
    MISSING: 'missing',
    diffs: [],
    SEPARATOR: '/',
    requestCount: 0,
}

export const DetailsModal = ({ id, onClose, methodName, options }: DetailsModalProps) => {
    jdd.EQUALITY = styles.eq.toString()
    const accentStyle = styles.styleAccent.toString()
    const compareInnerLeft = useRef<HTMLDivElement | null>(null)
    const compareInnerRight = useRef<HTMLDivElement | null>(null)
    const compareRef = useRef<() => void>(() => {})
    const [diffLines, setDiffLines] = useState<DiffLines | null>(null)
    const [comparedData, setComparedData] = useState<ComparedData | null>(null)
    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const [firstLeftAction, setFirstLeftAction] = useState<boolean>(false)
    const [firstRightAction, setFirstRightAction] = useState<boolean>(false)
    const [dataToCompare, setDataToCompare] = useState<DataToCompare | null>(null)

    const findDiffs = (config1: Config, data1: JsonObject, config2: Config, data2: JsonObject) => {
        config1.currentPath.push(jdd.SEPARATOR)
        config2.currentPath.push(jdd.SEPARATOR)

        Object.keys(data1).forEach((key) => {
            config1.currentPath.push(key.replace(jdd.SEPARATOR, '#'))

            if (!Object.prototype.hasOwnProperty.call(data2, key)) {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2),
                        'Missing property <code>' + key + '</code> from the object on the right side',
                        jdd.MISSING
                    )
                )
            } else {
                config2.currentPath.push(key.replace(jdd.SEPARATOR, '#'))

                diffVal(data1[key], config1, data2[key], config2)
                config2.currentPath.pop()
            }
            config1.currentPath.pop()
        })

        config1.currentPath.pop()
        config2.currentPath.pop()

        Object.keys(data2).forEach((key) => {
            if (!Object.prototype.hasOwnProperty.call(data1, key)) {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2, key),
                        'Missing property <code>' + key + '</code> from the object on the left side',
                        jdd.MISSING
                    )
                )
            }
        })
    }

    const diffVal = (val1: JsonValue, config1: Config, val2: JsonValue, config2: Config) => {
        if (Array.isArray(val1)) {
            diffArray(val1, config1, val2, config2)
        } else if (typeof val1 === 'object' && val1 !== null) {
            if (typeof val2 !== 'object' || val2 === null || Array.isArray(val2)) {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2),
                        'Both types should be objects',
                        jdd.TYPE
                    )
                )
            } else {
                findDiffs(config1, val1, config2, val2)
            }
        } else if (typeof val1 === 'string') {
            if (typeof val2 !== 'string') {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2),
                        'Both types should be strings',
                        jdd.TYPE
                    )
                )
            } else if (val1 !== val2) {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2),
                        'Both sides should be equal strings',
                        jdd.EQUALITY
                    )
                )
            }
        } else if (typeof val1 === 'number') {
            if (typeof val2 !== 'number') {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2),
                        'Both types should be numbers',
                        jdd.TYPE
                    )
                )
            } else if (val1 !== val2) {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2),
                        'Both sides should be equal numbers',
                        jdd.EQUALITY
                    )
                )
            }
        } else if (typeof val1 === 'boolean') {
            diffBool(val1, config1, val2, config2)
        } else if (val1 === null && val2 !== null) {
            jdd.diffs.push(
                generateDiff(
                    config1,
                    generatePath(config1),
                    config2,
                    generatePath(config2),
                    'Both types should be nulls',
                    jdd.TYPE
                )
            )
        }
    }
    const diffArray = (val1: JsonValue[], config1: Config, val2: JsonValue, config2: Config) => {
        if (!Array.isArray(val2)) {
            jdd.diffs.push(
                generateDiff(
                    config1,
                    generatePath(config1),
                    config2,
                    generatePath(config2),
                    'Both types should be arrays',
                    jdd.TYPE
                )
            )
            return
        }

        if (val1.length < val2.length) {
            for (let i = val1.length; i < val2.length; i++) {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2, '[' + i + ']'),
                        'Missing element <code>' + i + '</code> from the array on the left side',
                        jdd.MISSING
                    )
                )
            }
        }
        val1.forEach(function (_arrayVal, index) {
            if (val2.length <= index) {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1, '[' + index + ']'),
                        config2,
                        generatePath(config2),
                        'Missing element <code>' + index + '</code> from the array on the right side',
                        jdd.MISSING
                    )
                )
            } else {
                config1.currentPath.push(jdd.SEPARATOR + '[' + index + ']')
                config2.currentPath.push(jdd.SEPARATOR + '[' + index + ']')

                diffVal(val1[index], config1, val2[index], config2)
                config1.currentPath.pop()
                config2.currentPath.pop()
            }
        })
    }

    const diffBool = (val1: boolean, config1: Config, val2: JsonValue, config2: Config) => {
        if (typeof val2 !== 'boolean') {
            jdd.diffs.push(
                generateDiff(
                    config1,
                    generatePath(config1),
                    config2,
                    generatePath(config2),
                    'Both types should be booleans',
                    jdd.TYPE
                )
            )
        } else if (val1 !== val2) {
            if (val1) {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2),
                        'The left side is <code>true</code> and the right side is <code>false</code>',
                        jdd.EQUALITY
                    )
                )
            } else {
                jdd.diffs.push(
                    generateDiff(
                        config1,
                        generatePath(config1),
                        config2,
                        generatePath(config2),
                        'The left side is <code>false</code> and the right side is <code>true</code>',
                        jdd.EQUALITY
                    )
                )
            }
        }
    }

    const formatAndDecorate = (config: Config, data: JsonValue) => {
        if (Array.isArray(data)) {
            formatAndDecorateArray(config, data)
            return
        }

        if (typeof data !== 'object' || data === null) {
            formatVal(data, config)
            return
        }

        startObject(config)
        config.currentPath.push(jdd.SEPARATOR)

        const props = getSortedProperties(data)

        props.forEach(function (key: string) {
            config.out += newLine(config) + getTabs(config.indent) + '"' + unescapeString(key) + '": '
            config.currentPath.push(key.replace(jdd.SEPARATOR, '#'))
            config.paths.push({
                path: generatePath(config),
                line: config.line,
            })
            formatVal(data[key], config)
            config.currentPath.pop()
        })

        finishObject(config)
        config.currentPath.pop()
    }

    const formatAndDecorateArray = (config: Config, data: JsonValue[]) => {
        startArray(config)

        data.forEach(function (arrayVal, index) {
            config.out += newLine(config) + getTabs(config.indent)
            config.paths.push({
                path: generatePath(config, '[' + index + ']'),
                line: config.line,
            })

            config.currentPath.push(jdd.SEPARATOR + '[' + index + ']')
            formatVal(arrayVal, config)
            config.currentPath.pop()
        })

        finishArray(config)
        config.currentPath.pop()
    }

    const startArray = (config: Config) => {
        config.indent++
        config.out += '['

        if (config.paths.length === 0) {
            config.paths.push({
                path: generatePath(config),
                line: config.line,
            })
        }

        if (config.indent === 0) {
            config.indent++
        }
    }

    const finishArray = (config: Config) => {
        if (config.indent === 0) {
            config.indent--
        }

        removeTrailingComma(config)

        config.indent--
        config.out += newLine(config) + getTabs(config.indent) + ']'
        if (config.indent !== 0) {
            config.out += ','
        } else {
            config.out += newLine(config)
        }
    }

    const startObject = (config: Config) => {
        config.indent++
        config.out += '{'

        if (config.paths.length === 0) {
            config.paths.push({
                path: generatePath(config),
                line: config.line,
            })
        }

        if (config.indent === 0) {
            config.indent++
        }
    }

    const finishObject = (config: Config) => {
        if (config.indent === 0) {
            config.indent--
        }

        removeTrailingComma(config)

        config.indent--
        config.out += newLine(config) + getTabs(config.indent) + '}'
        if (config.indent !== 0) {
            config.out += ','
        } else {
            config.out += newLine(config)
        }
    }

    const formatVal = (val: JsonValue, config: Config) => {
        if (Array.isArray(val)) {
            config.out += '['

            config.indent++
            val.forEach((arrayVal, index) => {
                config.out += newLine(config) + getTabs(config.indent)
                config.paths.push({
                    path: generatePath(config, '[' + index + ']'),
                    line: config.line,
                })

                config.currentPath.push(jdd.SEPARATOR + '[' + index + ']')
                formatVal(arrayVal, config)
                config.currentPath.pop()
            })
            removeTrailingComma(config)
            config.indent--

            config.out += newLine(config) + getTabs(config.indent) + '],'
        } else if (typeof val === 'object' && val !== null) {
            formatAndDecorate(config, val)
        } else if (typeof val === 'string') {
            config.out += '"' + unescapeString(val) + '",'
        } else if (typeof val === 'number') {
            config.out += val + ','
        } else if (typeof val === 'boolean') {
            config.out += val + ','
        } else if (val === null) {
            config.out += 'null,'
        }
    }

    const unescapeString = (val: string) => {
        if (val) {
            return val
                .replace('\\', '\\\\') // Single slashes need to be replaced first
                .replace(/"/g, '\\"') // Then double quotes
                .replace(/\n/g, '\\n') // New lines
                .replace('\b', '\\b') // Backspace
                .replace(/\f/g, '\\f') // Formfeed
                .replace(/\r/g, '\\r') // Carriage return
                .replace(/\t/g, '\\t') // Horizontal tabs
        } else {
            return val
        }
    }

    const generatePath = (config: Config, prop?: string) => {
        let s = ''
        config.currentPath.forEach((path: string) => {
            s += path
        })

        if (prop) {
            s += jdd.SEPARATOR + prop.replace(jdd.SEPARATOR, '#')
        }

        if (s.length === 0) {
            return jdd.SEPARATOR
        } else {
            return s
        }
    }

    const newLine = (config: Config) => {
        config.line++
        return '\n'
    }

    const getSortedProperties = (obj: JsonObject) => {
        return Object.keys(obj).sort(function (a, b) {
            return a.localeCompare(b)
        })
    }

    const generateDiff = (
        config1: Config,
        path1: string,
        config2: Config,
        path2: string,
        msg: string,
        type: string
    ) => {
        if (path1 !== jdd.SEPARATOR && path1.charAt(path1.length - 1) === jdd.SEPARATOR) {
            path1 = path1.substring(0, path1.length - 1)
        }

        if (path2 !== jdd.SEPARATOR && path2.charAt(path2.length - 1) === jdd.SEPARATOR) {
            path2 = path2.substring(0, path2.length - 1)
        }
        const pathObj1 = config1.paths.find((path) => {
            return path.path === path1
        })
        const pathObj2 = config2.paths.find(function (path) {
            return path.path === path2
        })

        if (!pathObj1) {
            throw new Error('Unable to find line number for (' + msg + '): ' + path1)
        }

        if (!pathObj2) {
            throw new Error('Unable to find line number for (' + msg + '): ' + path2)
        }

        return {
            path1: pathObj1,
            path2: pathObj2,
            type: type,
            msg: msg,
        }
    }

    const getTabs = (indent: number) => {
        let s = ''
        for (let i = 0; i < indent; i++) {
            s += '    '
        }

        return s
    }

    const removeTrailingComma = (config: Config) => {
        if (config.out.charAt(config.out.length - 1) === ',') {
            config.out = config.out.substring(0, config.out.length - 1)
        }
    }

    const createConfig = () => {
        return {
            out: '',
            indent: -1,
            currentPath: [],
            paths: [],
            line: 1,
        }
    }

    const formatPRETags = () => {
        const preCollection = document.querySelectorAll('pre')

        if (preCollection) {
            const preElements = document.querySelectorAll('pre')

            if (preElements && preElements.length) {
                preElements.forEach(function (pre: HTMLPreElement) {
                    if (pre) {
                        let lineNumbers = '<div class="' + styles.gutter.toString() + '">'
                        let codeLines = '<div class="row">'

                        let lineDiv = document.createElement('div')
                        let lineText = document.createTextNode('')
                        lineDiv.appendChild(lineText)
                        const lineNumber = styles.lineNumber.toString()

                        let addLine = function (line: string, index: number) {
                            lineNumbers += '<span class="' + lineNumber + '">' + (index + 1) + '</span>'

                            lineText.nodeValue = line

                            codeLines +=
                                '<div class="codeLine line' +
                                (index + 1) +
                                '"><span class="code">' +
                                lineDiv.innerHTML +
                                '</span></div>'
                        }

                        const lines = (pre.textContent || '').split('\n')
                        lines.forEach(addLine)

                        codeLines += '</div>'
                        lineNumbers += '</div>'
                        const codeBlock = styles.codeBlock

                        let codeBlockElement =
                            '<pre id="' +
                            pre.id +
                            '" class="' +
                            codeBlock.toString() +
                            ' ' +
                            pre.classList.toString() +
                            '">' +
                            lineNumbers +
                            codeLines +
                            '</pre>'
                        pre.outerHTML = codeBlockElement
                    }
                })
            }
        }
    }

    const processDiffs = () => {
        const leftLineLookup: Record<number, Element> = {}
        const rightLineLookup: Record<number, Element> = {}

        document.querySelectorAll('pre.left span.code').forEach(function (val, index) {
            leftLineLookup[index + 1] = val
        })

        document.querySelectorAll('pre.right span.code').forEach(function (val, index) {
            rightLineLookup[index + 1] = val
        })

        jdd.diffs.forEach((diff) => {
            leftLineLookup[diff.path1.line]?.classList.add(diff.type, styles.diff.toString())
            rightLineLookup[diff.path2.line]?.classList.add(diff.type, styles.diff.toString())
        })

        jdd.diffs = jdd.diffs.sort((a, b) => {
            return a.path1.line - b.path1.line
        })
    }

    compareRef.current = () => {
        setTimeout(() => {
            if (jdd.requestCount !== 0) {
                return
            }

            jdd.diffs = []

            try {
                if (!dataToCompare) return

                const left = JSON.parse(JSON.stringify(dataToCompare.before)) as JsonValue
                const right = JSON.parse(JSON.stringify(dataToCompare.after)) as JsonValue

                const config = createConfig()
                const config2 = createConfig()
                formatAndDecorate(config, left)
                formatAndDecorate(config2, right)

                setComparedData({
                    before: config.out,
                    after: config2.out,
                })

                formatPRETags()

                config.currentPath = []
                config2.currentPath = []

                diffVal(left, config, right, config2)

                processDiffs()
            } catch (err) {
                console.log(err)
            }
        }, 100)
    }

    const onHighlightLeftDiff = (direction: string) => {
        if (!(diffLines && diffLines.left && diffLines.left.length)) return

        if (diffLines && diffLines.left[currentIndex]) {
            diffLines.left.forEach((element) => {
                if (element.element.classList.contains(accentStyle)) {
                    element.element.classList.remove(accentStyle)
                }
            })

            let nextIndex: number = 0

            if (direction === 'next') {
                if (firstLeftAction) {
                    if (diffLines.left[currentIndex + 1]) {
                        nextIndex = currentIndex + 1
                    }
                } else {
                    setFirstLeftAction(true)
                }
            }

            if (direction === 'prev') {
                if (diffLines.left[currentIndex - 1]) {
                    nextIndex = currentIndex - 1
                } else {
                    nextIndex = diffLines.left.length - 1
                }
            }

            diffLines.left[nextIndex].element.classList.add(accentStyle)
            if (compareInnerLeft.current) {
                compareInnerLeft.current.scrollTo({
                    top: diffLines.left[nextIndex].element.offsetTop - compareInnerLeft.current.offsetTop - 40,
                    left: 0,
                    behavior: 'smooth',
                })
            }

            setCurrentIndex(nextIndex)
        }
    }

    const onHighlightRightDiff = (direction: string) => {
        if (!(diffLines && diffLines.right && diffLines.right.length)) return

        if (diffLines && diffLines.right[currentIndex]) {
            diffLines.right.forEach((element) => {
                if (element.element.classList.contains(accentStyle)) {
                    element.element.classList.remove(accentStyle)
                }
            })

            let nextIndex: number = 0

            if (direction === 'next') {
                if (firstRightAction) {
                    if (diffLines.right[currentIndex + 1]) {
                        nextIndex = currentIndex + 1
                    }
                } else {
                    setFirstRightAction(true)
                }
            }

            if (direction === 'prev') {
                if (diffLines.right[currentIndex - 1]) {
                    nextIndex = currentIndex - 1
                } else {
                    nextIndex = diffLines.right.length - 1
                }
            }

            diffLines.right[nextIndex].element.classList.add(accentStyle)
            if (compareInnerRight.current) {
                compareInnerRight.current.scrollTo({
                    top: diffLines.right[nextIndex].element.offsetTop - compareInnerRight.current.offsetTop - 40,
                    left: 0,
                    behavior: 'smooth',
                })
            }

            setCurrentIndex(nextIndex)
        }
    }

    useEffect(() => {
        const method = methodName ? methodName : 'getHistoryEventDetails'
        const data = options ? options : { historyEventId: id }

        if (id) {
            api.send<Record<string, unknown>, HistoryDetailsResponse>(method, data).then((res) => {
                if (res) {
                    setDataToCompare({
                        before: res.before,
                        after: res.after,
                    })
                }
            })
        }
    }, [id, methodName, options])

    useEffect(() => {
        if (dataToCompare) {
            compareRef.current()
        }
    }, [dataToCompare])

    useEffect(() => {
        if (
            compareInnerLeft &&
            compareInnerLeft.current &&
            compareInnerRight &&
            compareInnerRight.current &&
            comparedData
        ) {
            const leftDiffs = compareInnerLeft.current.querySelectorAll(`.${styles.diff.toString()}`)
            const rightDiffs = compareInnerRight.current.querySelectorAll(`.${styles.diff.toString()}`)

            if (leftDiffs && leftDiffs.length && rightDiffs && rightDiffs.length) {
                const leftElements: DiffElement[] = []
                const rightElements: DiffElement[] = []

                leftDiffs.forEach((element, index) => {
                    if (!(element instanceof HTMLElement)) return

                    leftElements.push({
                        index,
                        top: element.getBoundingClientRect().y,
                        element,
                    })
                })
                rightDiffs.forEach((element, index) => {
                    if (!(element instanceof HTMLElement)) return

                    rightElements.push({
                        index,
                        top: element.getBoundingClientRect().y,
                        element,
                    })
                })

                setDiffLines({
                    left: leftElements,
                    right: rightElements,
                })
            }
        }
    }, [comparedData])

    return (
        <div className={styles.details}>
            <div className={styles.detailsInner}>
                <div className={styles.caption}>{translate('changesDetails')}</div>
                {/*<div className={styles.downloadButton}>*/}
                {/*    <Icon*/}
                {/*        type={'download_arrow'}*/}
                {/*        size={'24'}*/}
                {/*        tooltip={{ title: translate('download') }}*/}
                {/*        onClick={downloadHistoryDetails}*/}
                {/*    />*/}
                {/*</div>*/}
                {comparedData ? (
                    <div className={styles.compareContainers}>
                        <div className={styles.compareContainer}>
                            <div className={styles.compareContainerHeader}>
                                <div className={styles.compareTitle}>{translate('before')}</div>
                                <div className={styles.arrowsBlock}>
                                    <Icon
                                        type={'arrow_up'}
                                        size={12}
                                        className={styles.arrow}
                                        onClick={() => {
                                            onHighlightLeftDiff('prev')
                                        }}
                                    />
                                    <Icon
                                        type={'arrow_down'}
                                        size={12}
                                        className={styles.arrow}
                                        onClick={() => {
                                            onHighlightLeftDiff('next')
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={styles.compareInner} ref={compareInnerLeft}>
                                <pre className={'left'}>{comparedData.before}</pre>
                            </div>
                        </div>
                        <div className={styles.compareContainer}>
                            <div className={styles.compareContainerHeader}>
                                <div className={styles.compareTitle}>{translate('after')}</div>
                                <div className={styles.arrowsBlock}>
                                    <Icon
                                        type={'arrow_up'}
                                        size={12}
                                        className={styles.arrow}
                                        onClick={() => {
                                            onHighlightRightDiff('prev')
                                        }}
                                    />
                                    <Icon
                                        type={'arrow_down'}
                                        size={12}
                                        className={styles.arrow}
                                        onClick={() => {
                                            onHighlightRightDiff('next')
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={styles.compareInner} ref={compareInnerRight}>
                                <pre className={'right'}>{comparedData.after}</pre>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={styles.loader}>
                        <LoaderItem />
                    </div>
                )}
                <Button className={styles.closeButton} mod={'withBorder'} onClick={onClose}>
                    {translate('close')}
                </Button>
            </div>
        </div>
    )
}
